import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  HtmlEditor,
  Inject,
  RichTextEditorComponent,
  Toolbar,
  ToolbarSettingsModel,
  ToolbarType
} from "@syncfusion/ej2-react-richtexteditor";
import { Avatar, AvatarGroup, IconButton, TextField, Button } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";

import "./NoteEditor.styles.scss";
import { useDealActions, useDealIdSelector, useUserIdSelector, useViewActions } from "store";
import { TooltipDiv } from "components/tooltips/TooltipDiv";
import { useFormatDateHook } from "hooks";
import { Menu } from "components/popups";
import { INote } from "store/types";

export interface INoteData {
  value: string;
  title: string;
  RTLmode: boolean;
}
interface INoteEditor {
  note: INote | null;
  mode: "create" | "update";
  loading: boolean;
  onCancel: () => void;
  onSave: ({ value, title }: INoteData) => Promise<void>;
}

export interface IEditorEventData {
  name: string;
  value: string;
}

export const NoteEditor: React.FC<INoteEditor> = ({ note, mode, onCancel, onSave }): JSX.Element => {
  const toolbarSettings: ToolbarSettingsModel = {
    items: [
      "Bold",
      "Italic",
      "Underline",
      "justifyFull",
      "justifyLeft",
      "justifyCenter",
      "justifyRight",
      "UnorderedList",
      "OrderedList",
      {
        template:
          // eslint-disable-next-line quotes
          '<button class="e-tbar-btn e-control e-btn e-lib e-icon-btn"><span class="e-btn-icon e-icons custom-rtl"></span></button>',
        // undo: true,
        tooltipText: "RTL Mode",
        click: () => setRTLmode(false)
      },
      {
        template:
          // eslint-disable-next-line quotes
          '<button class="e-tbar-btn e-control e-btn e-lib e-icon-btn"><span class="e-btn-icon e-icons custom-ltr"></span></button>',
        tooltipText: "LTR Mode",
        click: () => setRTLmode(true)
      }
    ],

    type: ToolbarType.Expand
  };

  const formatDate = useFormatDateHook();
  const [value, setValue] = useState<string>("");
  const [RTLmode, setRTLmode] = useState<boolean>(false);
  const [title, setTitle] = useState<string>("");
  const editorRef = useRef<any>(null);
  const dealId = useDealIdSelector();
  const { updateNoteAction } = useDealActions();
  const { setShareDialog } = useViewActions();
  const userId = useUserIdSelector();

  useEffect(() => {
    if (mode === "update") {
      setValue(note?.text || "");
      setTitle(note?.title || "");
    }
    if (mode === "create") {
      setValue("");
      setTitle("");
    }
  }, [mode, note]);

  const getValue = useCallback((data: IEditorEventData) => {
    setValue(data.value);
  }, []);

  useEffect(() => {
    editorRef?.current?.addEventListener("change", getValue);
  }, [getValue]);

  return (
    <>
      <div className="note-create__wrapper">
        <div className="notes-create">
          <div className="notes-create__header">
            <TextField
              autoFocus={mode === "create"}
              placeholder="Note name"
              variant="standard"
              sx={{ width: "370px", marginBottom: "20px", borderColor: "#5d7782", margin: 0 }}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
              }}
            />
            {note?.id && (
              <div className="notes-item__privacy">
                {!note?.is_private && (
                  <div className=" notes-item__privacy-avatars">
                    {note?.shared_with && (
                      <AvatarGroup
                        sx={{
                          "& .MuiAvatar-root": { width: 24, height: 24, fontSize: 15 },
                          marginRight: "10px"
                        }}
                        max={10}
                      >
                        {note?.shared_with?.map((share, index) => (
                          <TooltipDiv key={share.id + index} place="bottom" tooltipMessage={share.full_name}>
                            <Avatar src={share.profile_photo_url} />
                          </TooltipDiv>
                        ))}
                      </AvatarGroup>
                    )}
                    <TooltipDiv tooltipMessage={"Share note"} place="top">
                      <IconButton
                        sx={{ padding: "8px", marginRight: "3px" }}
                        onClick={() => {
                          if (!dealId || !note?.id) return;
                          setShareDialog({
                            elements: [note.id],
                            type: "note_id",
                            shareName: title || "Note",
                            dealId: dealId,
                            itemToShare: note.is_private ? null : note
                          });
                        }}
                      >
                        <PersonAddOutlinedIcon sx={{ width: "20px", height: "20px" }} />
                      </IconButton>
                    </TooltipDiv>
                  </div>
                )}
                {note?.user_id === userId && (
                  <TooltipDiv tooltipMessage={note?.is_private ? "Private note" : "Note shared with team"} place="top">
                    <IconButton sx={{ padding: "0" }}>
                      <Menu
                        // triggerPadding="0px"
                        icon={
                          note?.is_private ? (
                            <LockOutlinedIcon sx={{ width: "20px", height: "20px" }} />
                          ) : (
                            <PublicOutlinedIcon sx={{ width: "20px", height: "20px" }} />
                          )
                        }
                        options={[
                          {
                            title: "Only me",
                            icon: <LockOutlinedIcon />,
                            onClick: () => {
                              if (note?.is_private || !note?.id) return;
                              updateNoteAction({ id: note.id, is_private: true });
                            }
                          },
                          {
                            title: "Share",
                            icon: <PublicOutlinedIcon />,
                            onClick: () => {
                              if (!note?.is_private || !note?.id) return;
                              updateNoteAction({ id: note.id, is_private: false });
                            }
                          }
                        ]}
                      />
                    </IconButton>
                  </TooltipDiv>
                )}
              </div>
            )}
          </div>
          <div className="note-text-editor">
            <div className="note-text-editor__input">
              <RichTextEditorComponent
                enableRtl={RTLmode}
                ref={editorRef}
                undoRedoTimer={300}
                className="rich-editor"
                // enableRtl={true} //RTL for Hebrew
                toolbarSettings={toolbarSettings}
                value={value}
                placeholder={"Please write your note here"}
              >
                <Inject services={[HtmlEditor, Toolbar]} />
              </RichTextEditorComponent>
              {/* <BinIcon className='rich-editor__bin scale-click' /> */}
            </div>
          </div>
          <div className="note-text-editor__toolbar">
            {mode === "update" ? (
              <span className="notes-item__date">
                {formatDate({ date: note?.created_at, format: "HH:mm, MMM DD, YYYY" })}
              </span>
            ) : (
              <span className="notes-item__date"></span>
            )}
            <div className="text-editor__toolbar-buttons">
              <Button className="text-editor__btn" variant="outlined" onClick={onCancel}>
                Cancel
              </Button>

              <Button
                className="text-editor__btn"
                disabled={value?.length > 60000}
                variant="contained"
                onClick={() => {
                  onSave({ value, title, RTLmode });
                }}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
